import React from 'react';
import { Scale, Sparkles, Users } from 'lucide-react';
import { useGovernedMcp } from '../governedMcpStore';
import type { ConfigSection } from '../governedMcpTypes';
import ClientsTab from './ClientsTab';
import PoliciesTab from './PoliciesTab';
import SetupTab from './SetupTab';

const SECTIONS: { id: ConfigSection; label: string; hint: string; icon: React.ComponentType<{ size?: number; className?: string }> }[] = [
  { id: 'setup', label: 'Setup', hint: 'Host and sequence', icon: Sparkles },
  { id: 'clients', label: 'Clients', hint: 'AI registrations', icon: Users },
  { id: 'policies', label: 'Policies', hint: 'Access rules', icon: Scale },
];

export default function ConfigurationTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, setConfigSection } = useGovernedMcp();
  const section = state.configSection;

  return (
    <div className="flex gap-5 items-start">
      <nav
        className={`w-52 shrink-0 rounded-xl border p-2 ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}
        aria-label="Configuration sections"
      >
        {SECTIONS.map((item) => {
          const Icon = item.icon;
          const active = section === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setConfigSection(item.id)}
              className={`w-full flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors ${
                active
                  ? isLight
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'bg-indigo-500/15 text-indigo-300'
                  : isLight
                    ? 'text-neutral-600 hover:bg-neutral-50'
                    : 'text-neutral-400 hover:bg-neutral-900'
              }`}
            >
              <Icon size={15} className="mt-0.5 shrink-0" />
              <span>
                <span className={`block text-sm font-medium ${active ? '' : isLight ? 'text-neutral-800' : 'text-neutral-200'}`}>{item.label}</span>
                <span className={`block text-[11px] mt-0.5 ${active ? (isLight ? 'text-indigo-500' : 'text-indigo-400/80') : isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
                  {item.hint}
                </span>
              </span>
            </button>
          );
        })}
      </nav>
      <div className="flex-1 min-w-0">
        {section === 'setup' ? <SetupTab isLight={isLight} onToast={onToast} /> : null}
        {section === 'clients' ? <ClientsTab isLight={isLight} onToast={onToast} /> : null}
        {section === 'policies' ? <PoliciesTab isLight={isLight} onToast={onToast} /> : null}
      </div>
    </div>
  );
}
